/**
 * TextureManager - Intelligent texture scaling and orientation for Three.js
 *
 * Handles two critical problems:
 * 1. Texture scaling: Adjusts texture.repeat based on real-world mesh dimensions
 * 2. Texture orientation: Rotates directional textures (wood grain) to align with longest mesh axis
 * 3. KTX2/Basis compressed texture loading for faster performance
 *
 * @example
 * const textureManager = new TextureManager();
 * textureManager.applyToMesh(mesh, {
 *   type: 'directional',
 *   unitsPerRepeat: 2.0, // Texture pattern repeats every 2 units
 *   materialConfig: materialConfigObject,
 *   useKTX2: true // Enable KTX2 compressed textures
 * });
 */

import * as THREE from 'three';
import { loadMaterialTextures } from './KTX2TextureLoader';
import { getCachedMaterial, isMaterialCached } from './materialCache';

export class TextureManager {
  constructor(renderer = null) {
    this.managedMeshes = new Map(); // Track meshes and their configurations
    this.materialTypes = {
      DIRECTIONAL: 'directional',     // Wood, brushed metal, fabric weave
      NON_DIRECTIONAL: 'non-directional' // Concrete, brick, stone
    };
    this.renderer = renderer; // Store renderer for KTX2 loading
    // Note: Texture cache is now handled by shared materialCache
  }

  /**
   * Set the WebGL renderer for KTX2 loading
   * @param {THREE.WebGLRenderer} renderer - The Three.js renderer
   */
  setRenderer(renderer) {
    this.renderer = renderer;
  }

  /**
   * Apply intelligent texture management to a mesh
   * @param {THREE.Mesh} mesh - The mesh to apply textures to
   * @param {Object} config - Configuration object
   * @param {string} config.type - 'directional' or 'non-directional'
   * @param {number} config.unitsPerRepeat - How many world units before texture repeats (e.g., 2.0 = grain repeats every 2 meters)
   * @param {Object} config.materialConfig - Material configuration from materialConfig.js
   * @param {string} [config.forceDirection] - Force grain direction: 'x', 'y', 'z' (overrides automatic detection)
   * @param {number} [config.rotationOffset] - Additional rotation in radians (default: 0)
   * @param {number} [config.squareThreshold] - Threshold for treating dimensions as equal (default: 1.2 = 20% difference)
   * @param {boolean} [config.useKTX2] - Use KTX2 compressed textures (default: true)
   */
  async applyToMesh(mesh, config) {
    if (!mesh.isMesh) {
      console.warn('TextureManager: Provided object is not a mesh');
      return;
    }

    if (!mesh.material) {
      console.warn('TextureManager: Mesh has no material');
      return;
    }

    // Store configuration for this mesh
    this.managedMeshes.set(mesh.uuid, {
      mesh,
      config,
      lastUpdate: Date.now()
    });

    // Load textures if materialConfig is provided and useKTX2 is enabled
    const useKTX2 = config.useKTX2 !== false; // Default to true
    if (config.materialConfig && useKTX2 && this.renderer) {
      await this.loadAndApplyTextures(mesh, config);
    }

    // Apply texture transformations
    this.updateMeshTextures(mesh, config);
  }

  /**
   * Load and apply KTX2 textures to a mesh
   * Uses shared materialCache - textures should already be preloaded by TexturePreloader
   * @private
   */
  async loadAndApplyTextures(mesh, config) {
    const { materialConfig } = config;

    // Find the material key from materialConfig
    // We need to match by basePath since config has the full materialConfig object
    const materialKeys = Object.keys(require('../configurator/materials/materialConfig').materialConfig);
    const materialKey = materialKeys.find(key => {
      const mat = require('../configurator/materials/materialConfig').materialConfig[key];
      return mat.basePath === materialConfig.basePath;
    });

    if (!materialKey) {
      console.warn(`⚠️ [TextureManager] Could not find material key for ${materialConfig.name}`);
      return;
    }

    // Check shared cache first (should already be loaded by TexturePreloader)
    let materialData = null;
    if (isMaterialCached(materialKey)) {
      console.log(`♻️ [TextureManager] Using pre-cached textures for ${materialConfig.name}`);
      materialData = getCachedMaterial(materialKey);
    } else {
      // Fallback: load if not in cache (shouldn't happen if TexturePreloader ran)
      console.warn(`⚠️ [TextureManager] Textures not preloaded for ${materialConfig.name}, loading now...`);
      try {
        const textures = await loadMaterialTextures(materialConfig, this.renderer, true);
        materialData = { textures, properties: materialConfig.properties };
      } catch (error) {
        console.error(`❌ [TextureManager] Failed to load textures for ${materialConfig.name}:`, error);
        return;
      }
    }

    if (!materialData || !materialData.textures) {
      console.error(`❌ [TextureManager] No texture data for ${materialConfig.name}`);
      return;
    }

    const textures = materialData.textures;

    // Apply textures to material
    if (textures.albedo) mesh.material.map = textures.albedo;
    if (textures.normal) mesh.material.normalMap = textures.normal;
    if (textures.roughness) mesh.material.roughnessMap = textures.roughness;
    if (textures.ao) mesh.material.aoMap = textures.ao;
    if (textures.height) mesh.material.displacementMap = textures.height;
    if (textures.metallic) mesh.material.metalnessMap = textures.metallic;

    mesh.material.needsUpdate = true;
  }

  /**
   * Update textures for a mesh based on its current dimensions
   * @private
   */
  updateMeshTextures(mesh, config) {
    const {
      type = this.materialTypes.NON_DIRECTIONAL,
      unitsPerRepeat = 2.0,
      materialConfig,
      forceDirection = null,
      rotationOffset = 0,
      squareThreshold = 1.2
    } = config;

    // Get mesh dimensions in world space
    const dimensions = this.getMeshDimensions(mesh);

    if (!dimensions) {
      console.warn('TextureManager: Could not compute mesh dimensions');
      return;
    }

    const { size, longestAxis, longestDimension, geometry } = dimensions;

    // Get all texture maps from material
    const textureProperties = this.getTextureProperties(mesh.material);

    if (textureProperties.length === 0) {
      console.warn('TextureManager: Material has no textures to manage');
      return;
    }

    // Calculate texture repeat based on real-world dimensions
    const repeatX = size.x / unitsPerRepeat;
    const repeatY = size.y / unitsPerRepeat;
    const repeatZ = size.z / unitsPerRepeat;

    // Determine rotation and scaling based on geometry type and orientation
    const textureTransform = this.calculateTextureTransform(
      geometry,
      size,
      longestAxis,
      longestDimension,
      { repeatX, repeatY, repeatZ },
      type,
      forceDirection,
      rotationOffset,
      squareThreshold,
      unitsPerRepeat
    );

    // Apply transformation to all texture maps
    textureProperties.forEach(({ texture, propertyName }) => {
      if (!texture) return;

      // Skip if texture hasn't loaded yet (no image data)
      if (!texture.image) {
        console.warn(`TextureManager: Skipping ${propertyName} - texture not loaded yet`);
        return;
      }

      // Apply repeat scaling
      texture.repeat.set(textureTransform.repeatU, textureTransform.repeatV);

      // Apply rotation (only for directional materials)
      if (type === this.materialTypes.DIRECTIONAL && textureTransform.rotation !== 0) {
        texture.rotation = textureTransform.rotation;
        // Set rotation center to middle of texture
        texture.center.set(0.5, 0.5);
      } else {
        texture.rotation = 0;
        texture.center.set(0, 0);
      }

      // Ensure proper wrapping
      texture.wrapS = THREE.RepeatWrapping;
      texture.wrapT = THREE.RepeatWrapping;

      texture.needsUpdate = true;
    });

    // Update material
    mesh.material.needsUpdate = true;

    console.log(`📐 TextureManager: Updated ${textureProperties.length} textures on ${mesh.name || 'unnamed mesh'}`);
    console.log(`   🎨 Type: ${type}`);
    console.log(`   📏 Dimensions: ${size.x.toFixed(0)}mm × ${size.y.toFixed(0)}mm × ${size.z.toFixed(0)}mm`);
    console.log(`   ➡️  Longest axis: ${longestAxis.toUpperCase()} (${longestDimension.toFixed(0)}mm)`);
    console.log(`   📐 UnitsPerRepeat: ${unitsPerRepeat}mm`);
    console.log(`   🔄 Texture repeat: U=${textureTransform.repeatU.toFixed(3)}, V=${textureTransform.repeatV.toFixed(3)}`);
    console.log(`   🔀 Rotation: ${(textureTransform.rotation * 180 / Math.PI).toFixed(1)}°`);
  }

  /**
   * Calculate mesh dimensions in world space
   * @private
   */
  getMeshDimensions(mesh) {
    // Compute bounding box
    if (!mesh.geometry.boundingBox) {
      mesh.geometry.computeBoundingBox();
    }

    const bbox = mesh.geometry.boundingBox.clone();

    // Apply mesh scale to bounding box
    bbox.min.multiply(mesh.scale);
    bbox.max.multiply(mesh.scale);

    const size = new THREE.Vector3();
    bbox.getSize(size);

    // Find longest axis
    let longestAxis = 'x';
    let longestDimension = size.x;

    if (size.y > longestDimension) {
      longestAxis = 'y';
      longestDimension = size.y;
    }
    if (size.z > longestDimension) {
      longestAxis = 'z';
      longestDimension = size.z;
    }

    return {
      size,
      longestAxis,
      longestDimension,
      geometry: mesh.geometry
    };
  }

  /**
   * Get all texture properties from a material
   * @private
   */
  getTextureProperties(material) {
    const textureProps = [];

    // Common PBR texture properties
    const texturePropertyNames = [
      'map',              // albedo/color
      'normalMap',        // normal
      'roughnessMap',     // roughness
      'metalnessMap',     // metalness
      'aoMap',            // ambient occlusion
      'displacementMap',  // height/displacement
      'emissiveMap',      // emissive
      'alphaMap'          // alpha/transparency
    ];

    texturePropertyNames.forEach(propName => {
      if (material[propName] && material[propName].isTexture) {
        textureProps.push({
          texture: material[propName],
          propertyName: propName
        });
      }
    });

    return textureProps;
  }

  /**
   * Calculate texture transformation based on geometry type and dimensions
   * @private
   */
  calculateTextureTransform(
    geometry,
    size,
    longestAxis,
    longestDimension,
    repeats,
    type,
    forceDirection,
    rotationOffset,
    squareThreshold,
    unitsPerRepeat
  ) {
    let repeatU, repeatV, rotation = 0;

    // Determine if object is nearly square (shouldn't rotate)
    const dimensions = [size.x, size.y, size.z].sort((a, b) => b - a);
    const aspectRatio = dimensions[0] / dimensions[1];
    const isNearlySquare = aspectRatio < squareThreshold;

    // Override longest axis if forced
    const effectiveLongestAxis = forceDirection || longestAxis;

    // Handle different geometry types
    if (geometry.type === 'CylinderGeometry' || geometry.type === 'CylinderBufferGeometry') {
      // For cylinders, grain should run along the height (Y axis in Three.js cylinder space)
      // The cylinder's local Y is the length
      return this.calculateCylinderTextureTransform(geometry, size, repeats, type);
    }

    // For BoxGeometry and PlaneGeometry, determine UV mapping orientation
    // Three.js default UV mapping:
    // - PlaneGeometry: X maps to U, Y maps to V
    // - BoxGeometry: Each face has its own UV mapping

    if (geometry.type === 'PlaneGeometry' || geometry.type === 'PlaneBufferGeometry') {
      // Plane: X -> U, Y -> V in local space
      // We want the texture to align with the longest dimension

      if (effectiveLongestAxis === 'x') {
        // Longest is X, texture already aligned correctly (grain horizontal)
        repeatU = repeats.repeatX;
        repeatV = repeats.repeatY;
        rotation = 0;
      } else if (effectiveLongestAxis === 'y') {
        // Longest is Y, texture aligned correctly (grain vertical)
        repeatU = repeats.repeatX;
        repeatV = repeats.repeatY;
        rotation = 0;
      } else {
        // Longest is Z (depth) - unusual for planes
        repeatU = repeats.repeatX;
        repeatV = repeats.repeatY;
        rotation = 0;
      }
    } else if (geometry.type === 'BoxGeometry' || geometry.type === 'BoxBufferGeometry') {
      // Box geometry - more complex, each face has different orientation
      // We'll set a general rule: align grain with longest dimension in XY plane for top face

      const xyLongest = size.x > size.y ? 'x' : 'y';
      const longestInPlane = size.x > size.y ? size.x : size.y;
      const shortestInPlane = size.x > size.y ? size.y : size.x;

      // Check if Z is longest (vertical beam)
      if (size.z > longestInPlane) {
        // Vertical beam - grain should run vertically on side faces
        // For top face, use XY orientation
        repeatU = repeats.repeatX;
        repeatV = repeats.repeatY;
        rotation = 0;
      } else if (effectiveLongestAxis === 'x') {
        // Horizontal beam along X
        repeatU = repeats.repeatX;
        repeatV = repeats.repeatY;
        rotation = 0;
      } else if (effectiveLongestAxis === 'y') {
        // Horizontal beam along Y - might need 90° rotation depending on UV mapping
        repeatU = repeats.repeatX;
        repeatV = repeats.repeatY;
        rotation = type === this.materialTypes.DIRECTIONAL ? Math.PI / 2 : 0;
      } else {
        // Z is longest
        repeatU = repeats.repeatX;
        repeatV = repeats.repeatZ;
        rotation = 0;
      }
    } else {
      // Custom or unknown geometry with box projection UVs
      // Our UV generation already orients U along the longest axis
      // So we just need to use the longest and an average of the other two

      // Sort dimensions to find longest, middle, shortest
      const dims = [
        { axis: 'x', size: size.x, repeat: repeats.repeatX },
        { axis: 'y', size: size.y, repeat: repeats.repeatY },
        { axis: 'z', size: size.z, repeat: repeats.repeatZ }
      ].sort((a, b) => b.size - a.size);

      // For world-space UVs (not normalized), texture.repeat should be 1/unitsPerRepeat
      // This makes the texture tile at the correct physical size
      repeatU = 1.0 / unitsPerRepeat;
      repeatV = 1.0 / unitsPerRepeat;

      console.log(`   📊 World-space UV mode:`);
      console.log(`      unitsPerRepeat: ${unitsPerRepeat}mm`);
      console.log(`      texture.repeat: ${repeatU.toFixed(6)} (uniform for both U and V)`);
      console.log(`      This means texture tiles every ${unitsPerRepeat}mm in world space`);

      // NO rotation for custom geometries - box projection UVs handle orientation
      rotation = 0;
    }

    // Apply rotation offset if provided
    rotation += rotationOffset;

    // Don't rotate if nearly square or non-directional
    if (isNearlySquare || type === this.materialTypes.NON_DIRECTIONAL) {
      rotation = rotationOffset; // Keep only the offset, not the automatic rotation
    }

    return { repeatU, repeatV, rotation };
  }

  /**
   * Calculate texture transform for cylinder geometry
   * @private
   */
  calculateCylinderTextureTransform(geometry, size, repeats, type) {
    // For cylinders, the standard UV mapping is:
    // - U wraps around the circumference
    // - V runs along the height (Y axis)

    // We want wood grain to run along the height (length of the cylinder)
    const radius = Math.max(size.x, size.z) / 2;
    const height = size.y;
    const circumference = 2 * Math.PI * radius;

    // V should map to height, U should map to circumference
    const repeatU = circumference / repeats.repeatX;
    const repeatV = height / repeats.repeatY;

    return {
      repeatU,
      repeatV,
      rotation: 0 // Cylinders don't need rotation, grain already aligned with V
    };
  }

  /**
   * Update all managed meshes (call when dimensions change)
   */
  updateAll() {
    this.managedMeshes.forEach(({ mesh, config }) => {
      this.updateMeshTextures(mesh, config);
    });
  }

  /**
   * Update a specific mesh by UUID or reference
   */
  updateMesh(meshOrUuid) {
    const uuid = typeof meshOrUuid === 'string' ? meshOrUuid : meshOrUuid.uuid;
    const entry = this.managedMeshes.get(uuid);

    if (entry) {
      this.updateMeshTextures(entry.mesh, entry.config);
    } else {
      console.warn(`TextureManager: Mesh ${uuid} not found in managed meshes`);
    }
  }

  /**
   * Remove a mesh from management
   */
  removeMesh(meshOrUuid) {
    const uuid = typeof meshOrUuid === 'string' ? meshOrUuid : meshOrUuid.uuid;
    this.managedMeshes.delete(uuid);
  }

  /**
   * Clear all managed meshes
   */
  clear() {
    this.managedMeshes.clear();
  }

  /**
   * Get material type enum
   */
  get types() {
    return this.materialTypes;
  }
}

/**
 * Helper function to determine if a material represents directional textures
 * @param {string} materialKey - Material key from materialConfig
 * @returns {string} - 'directional' or 'non-directional'
 */
export function getMaterialType(materialKey) {
  const directionalMaterials = [
    'oak-wood',
    'streaky-plywood',
    'brushed-metal',
    'fabric-weave',
    'pine-wood',
    'walnut-wood',
    'bamboo',
    'cedar',
    'mahogany'
  ];

  const nonDirectionalMaterials = [
    'grainy-concrete',
    'grass',
    'brick',
    'stone',
    'marble',
    'tile',
    'gravel',
    'sand'
  ];

  if (directionalMaterials.some(mat => materialKey.includes(mat))) {
    return 'directional';
  }

  if (nonDirectionalMaterials.some(mat => materialKey.includes(mat))) {
    return 'non-directional';
  }

  // Default: if it contains 'wood', treat as directional
  if (materialKey.includes('wood') || materialKey.includes('plywood')) {
    return 'directional';
  }

  // Default to non-directional
  return 'non-directional';
}

/**
 * Create a singleton instance for global use
 * Note: Call setRenderer(renderer) after creating your Three.js renderer
 */
export const globalTextureManager = new TextureManager();

/**
 * Cleanup texture cache and dispose all cached textures
 */
export function disposeTextureCache() {
  // Note: Texture cache is now managed by shared materialCache
  // Individual textures are managed by KTX2TextureLoader's textureCache
  globalTextureManager.managedMeshes.clear();
  console.log('🧹 TextureManager cleared');
}
